/**
 * W-D-01 — staff credential login (D-01/A-01/S-01, api-contract.md §2.2).
 *
 * Email + password. On `must_change_password`, redirect to the change-password screen
 * before the dashboard. Surfaces INVALID_CREDENTIALS / ACCOUNT_LOCKED inline.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { staffLogin } from "../../api/auth";
import { ApiError } from "../../api/errors";
import { roleHome, useAuth } from "../../auth/AuthContext";
import { Logo } from "../../components/Logo";

export function StaffLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await staffLogin(email.trim(), password);
      login(
        {
          id: result.user.id,
          role: result.user.role,
          full_name: result.user.full_name,
          must_change_password: result.user.must_change_password,
        },
        result.access_token && result.refresh_token
          ? { access: result.access_token, refresh: result.refresh_token }
          : undefined,
      );
      if (result.user.must_change_password) {
        navigate("/auth/change-password", { replace: true });
      } else {
        navigate(roleHome(result.user.role), { replace: true });
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-sunken px-4 py-10">
      <div className="brand-gradient absolute inset-x-0 top-0 h-1.5" aria-hidden />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-brand-50 via-brand-50/40 to-transparent"
        aria-hidden
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo variant="stacked" size={58} withTagline to="/" />
        </div>

        <div className="card overflow-hidden">
          <div className="brand-gradient h-1" aria-hidden />
          <div className="p-6">
            <h1 className="mb-1 text-xl font-bold text-brand-900">Staff sign in</h1>
            <p className="mb-6 text-sm text-ink-muted">Use the credentials issued to you.</p>

            {error && (
              <div className="mb-4 rounded-xl border border-danger/20 bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
                {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="staff-email" className="label">
                  Email
                </label>
                <input
                  id="staff-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@pharmacy.com"
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="staff-password" className="label">
                  Password
                </label>
                <input
                  id="staff-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="input"
                  required
                />
              </div>
              <button type="submit" disabled={busy} className="btn-primary btn-block">
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-ink-muted">Express care. Always there.</p>
      </div>
    </main>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "INVALID_CREDENTIALS":
        return "Incorrect email or password.";
      case "ACCOUNT_LOCKED":
        return "Your account is locked. Contact an administrator.";
      case "RATE_LIMIT_EXCEEDED":
        return "Too many attempts. Please try again later.";
      default:
        return err.message;
    }
  }
  return "Something went wrong. Please try again.";
}
